<template>
    <NuxtLink
        :to="article?.urls ?? ''"
        class="sim-article-card"
        :class="{
            'sim-article-card--main': type === 'main',
        }"
    >
        <div class="sim-article-card__image">
            <CoreImg
                :src="article?.thumbnailImageUrl ?? article?.imageUrl"
                :alt="article?.name"
                :width="type === 'main' ? 1125 : 545"
                :height="type === 'main' ? 708 : 352"
                loading="lazy"
                :sizes="type === 'main' ? '100vw sm:100vw md:50vw lg:50vw xl:50vw xxl:50vw' : '100vw sm:100vw md:50vw lg:30vw xl:30vw xxl:30vw'"
                :placeholder="[54, 35, 100, 4]"
                class="size-full"
            />
        </div>

        <div v-if="article?.tags" class="sim-article-card__labels flex flex-wrap gap-4">
            <BaseUiBadge v-for="(tag, index) in article?.tags"
                         :key="index"
                         color="simplo"
                         content-class="font-bold uppercase"
                         pill
            >
                {{ tag }}
            </BaseUiBadge>
        </div>

        <div class="sim-article-card__content">
            <h2 class="sim-article-card__title mb-3 block">
                {{ article?.name }}
            </h2>

            <div class="sim-article-card__text">
                {{ article?.perex }}
            </div>
        </div>

        <BlogArticleCardDetails :author="article?.user ?? null" :time="article?.timeForReading" class="sim-article-card__author mb-12" />
    </NuxtLink>
</template>

<script lang="ts" setup>

const {
    article,
    type = 'normal',
} = defineProps<{
    article?: InstanceType<typeof ArticleModel> | null
    type?: 'normal' | 'main'
}>()

</script>

<style lang="scss" scoped>

.sim-article-card {
    position: relative;

    display: flex;
    flex-direction: column;
    justify-content: space-between;

    gap: 1rem;

    height: 100%;
}

.sim-article-card--main {
    @include more-than(lg) {
        justify-content: end;

        min-height: 40rem;

        color: $sim-c-white;
    }

    @include more-than(lg) {
        grid-column: span 2;
    }
}

.sim-article-card__content {
    flex: 1;
}

.sim-article-card--main .sim-article-card__content {
    @include more-than(lg) {
        flex: 0;
        margin: 0 2.5rem;
    }
}

.sim-article-card__labels {
    position: absolute;
    top: 1.25rem;
    left: 1.25rem;
}

.sim-article-card--main .sim-article-card__labels {
    @include more-than(lg) {
        position: static;

        margin: 0 2.5rem;

        width: fit-content;
    }
}

.sim-article-card__image {
    display: flex;
    align-items: center;
    justify-content: center;

    height: 22rem;
    border-radius: $sim-border-radius-sm;

    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;

        transition: transform $sim-trans-time-long $sim-timing;
    }
}

.sim-article-card:hover .sim-article-card__image img {
    transform: scale(1.04);
}

.sim-article-card--main .sim-article-card__image {
    @include more-than(lg) {
        position: absolute;
        inset: 0;

        height: auto;

        z-index: -1;

        &::before {
            content: "";
            position: absolute;
            inset: 0;
            z-index: 1;
            background: linear-gradient(rgb(255 255 255 / 0) 30%, $sim-c-primary-900 100%);
        }
    }
}

.sim-article-card--main:hover .sim-article-card__image img {
    @include more-than(lg) {
        transform: scale(1.025);
    }
}

.sim-article-card__title {
    @include sim-h5;
}

.sim-article-card--main .sim-article-card__title {
    @include more-than(lg) {
        @include sim-h1;
    }
}

.sim-article-card__text {
    @include sim-text-large;
    line-height: 200%;
}

.sim-article-card--main .sim-article-card__author {
    @include more-than(lg) {
        margin: 0 2.5rem 2.5rem;
    }
}

</style>
